// Dialog de criação/edição de agendamento (§4.3) — RHF + Zod:
// direção (ANTES/DEPOIS), dias (>= 1) e template por canal
// (opcional, um select por canal ativo do WL). O write é otimista
// (configuration-mutations), então o dialog fecha já no mutate.
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type {
  NotificationEventSchedulerType,
  NotificationEventTemplateRefType,
  NotificationOriginTransactionType,
  NotificationSearchChannelType,
  NotificationWhiteLabelChannelProviderType,
} from "@/types/notification-center"

import {
  useCreateEventScheduler,
  useUpdateEventScheduler,
} from "../configuration-mutations"
import { useNotificationCenterWhiteLabelsTemplates } from "../hooks"
import { dedupeTemplateOptionsByLatest } from "../template-options"

const formPrefix =
  "pages.notificationCenter.configurationsDetailPage.schedulerForm"

// Sentinela do Radix Select p/ "sem template" (value="" é inválido).
const NO_TEMPLATE = "NO_TEMPLATE"

// Schema do form — days inteiro >= 1 (coerce: input number → string).
const buildSchedulerFormSchema = (daysMinMessage: string) =>
  z.object({
    direction: z.enum(["BEFORE", "AFTER"]),
    days: z.coerce
      .number({ invalid_type_error: daysMinMessage })
      .int(daysMinMessage)
      .min(1, daysMinMessage),
    templates: z.record(z.string()),
  })
type SchedulerFormValues = z.infer<
  ReturnType<typeof buildSchedulerFormSchema>
>

type SchedulerFormDialogProps = {
  whiteLabelId: string
  eventName: string
  // Linha do card do evento (ORDER/SUBSCRIPTION); undefined = antigo.
  transactionType?: NotificationOriginTransactionType
  activeChannels: NotificationWhiteLabelChannelProviderType[]
  // Presente = edição; ausente = criação.
  scheduler?: NotificationEventSchedulerType
  onClose: () => void
}

export const SchedulerFormDialog = ({
  whiteLabelId,
  eventName,
  transactionType,
  activeChannels,
  scheduler,
  onClose,
}: SchedulerFormDialogProps) => {
  const { t } = useTranslation()
  const isEdit = Boolean(scheduler)
  const createScheduler =
    useCreateEventScheduler(whiteLabelId)
  const updateScheduler =
    useUpdateEventScheduler(whiteLabelId)
  // Opções de template do WL (o dialog só monta aberto → 1 fetch).
  const { data: templateOptions } =
    useNotificationCenterWhiteLabelsTemplates({
      whiteLabelId,
    })
  // Uma opção por template (versão ativa/maior) — evita valores
  // duplicados no Select, que estouram o trigger com texto concatenado.
  const uniqueTemplateOptions =
    dedupeTemplateOptionsByLatest(templateOptions ?? [])

  const defaultTemplates = Object.fromEntries(
    activeChannels.map(({ channel }) => [
      channel,
      scheduler?.templates.find(
        (template) => template.channel === channel
      )?.uuid ?? NO_TEMPLATE,
    ])
  )
  const form = useForm<SchedulerFormValues>({
    resolver: zodResolver(
      buildSchedulerFormSchema(t(`${formPrefix}.daysMin`))
    ),
    defaultValues: {
      direction: scheduler?.direction ?? "BEFORE",
      days: scheduler?.days ?? 1,
      templates: defaultTemplates,
    },
  })

  const handleSubmit = form.handleSubmit((values) => {
    const payloadTemplates = activeChannels.flatMap(
      ({ channel }) => {
        const templateUuid = values.templates[channel]
        return templateUuid && templateUuid !== NO_TEMPLATE
          ? [{ channel, templateUuid }]
          : []
      }
    )
    // Shape de exibição p/ o cache otimista (nome@versão).
    const optimisticTemplates: NotificationEventTemplateRefType[] =
      payloadTemplates.map(({ channel, templateUuid }) => {
        const option = templateOptions?.find(
          (template) => template.id === templateUuid
        )
        const existing = scheduler?.templates.find(
          (template) => template.uuid === templateUuid
        )
        return {
          channel: channel as NotificationSearchChannelType,
          uuid: templateUuid,
          name:
            option?.name ?? existing?.name ?? templateUuid,
          version:
            option?.version ?? existing?.version ?? 1,
        }
      })
    const payload = {
      direction: values.direction,
      days: values.days,
      enabled: scheduler?.enabled ?? true,
      templates: payloadTemplates,
    }
    if (isEdit && scheduler) {
      updateScheduler.mutate({
        eventName,
        transactionType,
        schedulerUuid: scheduler.uuid,
        payload,
        optimisticTemplates,
      })
    } else {
      createScheduler.mutate({
        eventName,
        transactionType,
        payload,
        optimisticTemplates,
      })
    }
    // Write otimista: fecha já — erro faz rollback + toast.
    onClose()
  })

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(
              `${formPrefix}.${isEdit ? "editTitle" : "createTitle"}`
            )}
          </DialogTitle>
          <DialogDescription>
            {t(`${formPrefix}.description`)}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="direction"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(`${formPrefix}.direction`)}
                  </FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="flex gap-6"
                    >
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem
                            value="BEFORE"
                            id={`direction-before-${eventName}`}
                          />
                        </FormControl>
                        <FormLabel
                          htmlFor={`direction-before-${eventName}`}
                          className="font-normal"
                        >
                          {t(`${formPrefix}.before`)}
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem
                            value="AFTER"
                            id={`direction-after-${eventName}`}
                          />
                        </FormControl>
                        <FormLabel
                          htmlFor={`direction-after-${eventName}`}
                          className="font-normal"
                        >
                          {t(`${formPrefix}.after`)}
                        </FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="days"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(`${formPrefix}.days`)}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      className="tabular-nums"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <fieldset className="space-y-3">
              <legend className="text-sm font-medium">
                {t(`${formPrefix}.templatesTitle`)}
              </legend>
              {activeChannels.map(({ channel }) => (
                <FormField
                  key={channel}
                  control={form.control}
                  name={`templates.${channel}`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{channel}</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger
                            className="w-full max-w-full [&>span]:truncate"
                            aria-label={t(
                              `${formPrefix}.templateFor`,
                              { channel }
                            )}
                          >
                            <SelectValue
                              placeholder={t(
                                `${formPrefix}.noTemplate`
                              )}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={NO_TEMPLATE}>
                            {t(`${formPrefix}.noTemplate`)}
                          </SelectItem>
                          {uniqueTemplateOptions.map(
                            (template) => (
                              <SelectItem
                                key={template.id}
                                value={template.id}
                              >
                                <span className="truncate">
                                  {template.name} @v
                                  {template.version}
                                </span>
                              </SelectItem>
                            )
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </fieldset>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                {t(`${formPrefix}.cancel`)}
              </Button>
              <Button type="submit">
                {t(`${formPrefix}.save`)}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
